import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  ClipboardList,
  ExternalLink,
  History,
  Pen,
  Send,
  Undo2,
} from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  DatePicker,
  DropdownField,
  Label,
  Textarea,
} from '@roger/ui-components';
import {
  approveTask,
  breakTaskSignatures,
  getPatientCommunication,
  getPatientCommunicationPdf,
  getTaskSignatures,
  getThirtyDaySummaryPdf,
  returnTaskToClinician,
  signTask,
  updatePatientCommunication,
} from '../../services/api';
import { listAgencyClinicPhysiciansAgencyClinicsAgencyClinicIdPhysiciansGet as listClinicPhysicians } from '../../services/api/sdk.gen';
import type {
  ClinicPhysicianResponse,
  PatientCommunicationResponse,
  TaskSignatureResponse,
} from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { usePdfTab } from '../../hooks/usePdfTab';
import { useToast } from '../../hooks/useToastContext';
import { useAllowSubmitWithoutSignature } from '../../hooks/useAllowSubmitWithoutSignature';
import { useAutoSave, SaveDeclinedError } from '../../hooks/useAutoSave';
import {
  getSignButtonLabel,
  useTaskPermissions,
} from '../task/TaskDetailPanel';
import {
  TaskHeaderControls,
  TaskWorkflowStatusBadge,
} from '../task/TaskHeaderControls';
import { AttestationDialog } from '../signing/AttestationDialog';
import { THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE } from './patientCommunicationTypes';
import { ReadonlyPdfView } from '../documents/ReadonlyPdfView';
import type {
  ESignatureCredentialInput,
  SignatureDateInput,
} from '../signing/esignatureCredentialApi';
import { PermissionSlug } from '../../constants/featureFlags';
import { SignatureHistoryDialog } from '../signing/SignatureHistoryDialog';
import { WorkLogDialog } from '../task/WorkLogDialog';
import { useDeferredDialogOpener } from '../task/useDeferredDialogOpener';
import {
  resolveAttestationFamily,
  selectAttestationForFamily,
} from '../signing/attestations';
import { useActiveAttestations } from '../signing/useActiveAttestations';
import { signerName } from '../signing/signatureSummary';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import EpisodeNotes from '../episode/EpisodeNotes';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { formatPhysicianName } from '../../utils/formatting';
import { AutoSavePill } from '../AutoSavePill';
import { useTaskSignatureBypasses } from '../signing/useTaskSignatureBypasses';
import { useSignatureBypassAction } from '../signing/useSignatureBypassAction';

interface CommunicationDetailPanelProps {
  communicationId: string;
  /** Only used to resolve the signing date in the patient's clinic zone. */
  patientId?: string;
  onBack?: () => void;
  onUpdated?: () => void;
  /**
   * This communication is soft-deleted, opened from the deleted-tasks view.
   * Widens the reads that would otherwise 404 and makes the panel strictly read
   * only: no form, no autosave, no signing, no unlock. The Actions menu keeps
   * only print view, signature history and the work log.
   */
  isDeleted?: boolean;
}

type CommunicationDetailDraft = {
  communication_date: string | null;
  clinic_physician_id: string;
  notes: string;
};

const emptyCommunicationDetailDraft: CommunicationDetailDraft = {
  communication_date: null,
  clinic_physician_id: '',
  notes: '',
};

const communicationDetailDraftFromResponse = (
  comm: PatientCommunicationResponse
): CommunicationDetailDraft => ({
  communication_date: comm.communication_date,
  clinic_physician_id: comm.clinic_physician_id ?? '',
  notes: comm.notes ?? '',
});

// useAutoSave defaults to Object.is. This draft is a new object on every fetch /
// PUT response, so reference equality would treat every echo as a fresh server
// snapshot and rewrite the textarea after each save.
const communicationDetailDraftsEqual = (
  a: CommunicationDetailDraft,
  b: CommunicationDetailDraft
): boolean =>
  a.communication_date === b.communication_date &&
  a.clinic_physician_id === b.clinic_physician_id &&
  // Save persists notes.trim(); compare trimmed so a trailing space does not
  // look unequal to the echo and get rewritten into the textarea after save.
  a.notes.trim() === b.notes.trim();

export const CommunicationDetailPanel: React.FC<
  CommunicationDetailPanelProps
> = ({ communicationId, patientId, onBack, onUpdated, isDeleted = false }) => {
  const { error: showError } = useToast();
  const user = useStore(state => state.user);
  const allowUnsignedMoves = useAllowSubmitWithoutSignature();
  const openPdfTab = usePdfTab();
  const {
    attestations,
    isLoading: attestationsLoading,
    error: attestationsError,
  } = useActiveAttestations();

  useEffect(() => {
    if (attestationsError) {
      showError(
        'Could not load signing text',
        'Refresh the page or try again in a moment.'
      );
    }
  }, [attestationsError, showError]);

  const [communication, setCommunication] =
    useState<PatientCommunicationResponse | null>(null);
  const [signatures, setSignatures] = useState<TaskSignatureResponse[]>([]);
  // ROG-3269: a bypassed chart must not render as normally signed. Keyed on an
  // explicit nonce rather than `signatures.length` — a bypass writes no
  // signature, so a count would never change and the notice would not appear
  // until the panel remounted.
  const [signatureRefreshNonce, setSignatureRefreshNonce] = useState(0);
  const signatureBypasses = useTaskSignatureBypasses(
    communication?.id,
    signatureRefreshNonce
  );
  const [loading, setLoading] = useState(true);
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);

  const [signingLoading, setSigningLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [returning, setReturning] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const [approveAttestationOpen, setApproveAttestationOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const pdfInFlightRef = useRef(false);
  const [signAttestationOpen, setSignAttestationOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const openDeferred = useDeferredDialogOpener();
  const [workLogOpen, setWorkLogOpen] = useState(false);
  const canViewWorkLog = useStore(state =>
    state.hasPermission(PermissionSlug.WORKLOG_VIEW)
  );
  const [savedDraft, setSavedDraft] = useState<CommunicationDetailDraft>(
    emptyCommunicationDetailDraft
  );
  // A locked communication still accepts a physician change, but its autosave is
  // not mounted to carry one, so the readonly picker writes on its own. The
  // pending value holds the control on the user's choice until the write lands.
  const [pendingPhysicianId, setPendingPhysicianId] = useState<string | null>(
    null
  );
  const [savingPhysician, setSavingPhysician] = useState(false);
  // The disabled state only reaches the control on the next render, so a second
  // selection in the same tick would otherwise start a second write.
  const savingPhysicianRef = useRef(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [commRes, sigsRes] = await Promise.all([
        getPatientCommunication({
          path: { communication_id: communicationId },
          ...(isDeleted && { query: { include_deleted: true } }),
        }),
        getTaskSignatures({
          path: { task_id: communicationId },
          ...(isDeleted && { query: { include_deleted: true } }),
        }),
      ]);
      if (commRes.data) {
        setCommunication(commRes.data);
        setSavedDraft(communicationDetailDraftFromResponse(commRes.data));
      }
      if (sigsRes.data) {
        setSignatures(sigsRes.data);
      }
      setSignatureRefreshNonce(n => n + 1);
    } catch (err) {
      console.error('Error fetching communication:', err);
      showError('Load Failed', 'Failed to load communication details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, [communicationId, isDeleted]);

  useEffect(() => {
    if (!communication?.agency_clinic_id) return;
    const clinicId = communication.agency_clinic_id;
    let cancelled = false;
    (async () => {
      try {
        const res = await listClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled && res.data) {
          setPhysicians(res.data as ClinicPhysicianResponse[]);
        }
      } catch (err) {
        console.error('Failed to load physicians:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [communication?.agency_clinic_id]);

  // A deleted communication is never editable, whatever status it was deleted
  // in. Deriving read-only from this (rather than only hiding controls) is what
  // keeps the editable form and its autosave off the page entirely.
  const isReadonly =
    isDeleted || !EDITABLE_STATUSES.has(communication?.status ?? 'not_started');

  // A 30-Day Summary keeps its clinical content in communication_data, which
  // this panel's read-only body does not render — it shows date, type and notes,
  // which is all an ordinary communication has. A live summary never lands here
  // (PatientDetail routes it to its own panel), but a deleted one does, because
  // that routing is resolved from the live communications list. Rather than
  // teach the editing panel to be read-only, render what a signed summary shows
  // there: the generated print view, which is the whole of the content.
  const isThirtyDaySummary =
    communication?.communication_type === THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE;

  const loadSummaryPdfSource = React.useCallback(async () => {
    if (!communication) return null;
    const res = await getThirtyDaySummaryPdf({
      path: { communication_id: communication.id },
      ...(isDeleted && { query: { include_deleted: true } }),
    });
    if (res.error || !res.data) return null;
    const url = URL.createObjectURL(
      new Blob([res.data as BlobPart], { type: 'application/pdf' })
    );
    return { src: url, cleanup: () => URL.revokeObjectURL(url) };
  }, [communication, isDeleted]);

  // Role-based action gating, shared with Visit/Order panels so a communication
  // signs, approves and unlocks exactly like every other task (ROG-2553).
  const {
    canSign: canSignTask,
    canApprove: canApproveTask,
    canBreakSignatures: canBreakTaskSignatures,
    canBypassSignature: canBypassTaskSignature,
    canReturnToClinician: canReturnTaskToClinician,
    signRequiresESignature,
    signTargetStatus,
  } = useTaskPermissions(user, communication ?? undefined, {
    allowSubmitWithoutSignature: allowUnsignedMoves,
  });

  // A deleted communication forfeits every workflow action, regardless of role.
  // Zeroed here rather than at each use site so a new action added below
  // inherits the suppression instead of having to remember it.
  const canSign = !isDeleted && canSignTask;
  const canApprove = !isDeleted && canApproveTask;
  const canBreakSignatures = !isDeleted && canBreakTaskSignatures;
  const canBypassSignature = !isDeleted && canBypassTaskSignature;
  const canReturnToClinician = !isDeleted && canReturnTaskToClinician;

  // A communication awaiting a departed clinician strands the same way a visit does.
  const { actionItems: bypassActionItems, dialog: bypassDialog } =
    useSignatureBypassAction({
      taskId: communication?.id,
      canBypass: canBypassSignature,
      onBypassed: fetchData,
    });

  const communicationAttestation = useMemo(() => {
    if (!attestations) return null;
    return selectAttestationForFamily(
      attestations,
      resolveAttestationFamily('patient_communication')
    );
  }, [attestations]);

  const activeSignatures = useMemo(
    () => signatures.filter(sig => !sig.invalidated_at),
    [signatures]
  );

  const handleAutoSave = React.useCallback(
    async (draft: CommunicationDetailDraft) => {
      if (
        !communication ||
        !draft.communication_date ||
        !draft.notes.trim() ||
        !draft.clinic_physician_id
      ) {
        // Required fields missing — refuse the save by throwing rather than
        // returning, so useAutoSave doesn't record the incomplete draft as the
        // saved baseline (which would make hasUnsavedChanges lie and drop the
        // next real save). The readiness guard still keeps the pill quiet until
        // the form is complete; surfacing "Not saved" on this sticky pill is a
        // separate UX call left to the comms-pill follow-up.
        throw new SaveDeclinedError();
      }

      try {
        const res = await updatePatientCommunication({
          path: { communication_id: communicationId },
          body: {
            communication_date: draft.communication_date,
            communication_type: communication.communication_type,
            assigned_clinician_id: communication.assigned_clinician_id ?? null,
            clinic_physician_id: draft.clinic_physician_id,
            notes: draft.notes.trim(),
            status: communication.status,
          },
        });
        if (!res.data) {
          throw new Error('Communication update returned no data.');
        }
        setCommunication(res.data);
        // Keep initialValue on the row we just wrote. Leaving the fetch-time
        // savedDraft in place makes useAutoSave adopt that stale object once
        // the field goes clean after save, which reverts the notes textarea
        // while the pill still shows Autosaved.
        setSavedDraft(communicationDetailDraftFromResponse(res.data));
        onUpdated?.();
      } catch (err) {
        console.error('Error saving communication:', err);
        showError('Save Failed', 'Could not save changes.');
        throw err;
      }
    },
    [communication, communicationId, onUpdated, showError]
  );

  const {
    value: editDraft,
    setValue: setEditDraft,
    saveStatus,
    saveNow,
    changeToken,
  } = useAutoSave({
    initialValue: savedDraft,
    onSave: handleAutoSave,
    isEqual: communicationDetailDraftsEqual,
  });

  const editDate = useMemo(
    () =>
      editDraft.communication_date
        ? parseISOToLocalDate(editDraft.communication_date)
        : undefined,
    [editDraft.communication_date]
  );

  const updateEditDraft = React.useCallback(
    (patch: Partial<CommunicationDetailDraft>) => {
      setEditDraft({ ...editDraft, ...patch });
    },
    [editDraft, setEditDraft]
  );

  const isAutosaveReady = Boolean(
    editDraft.communication_date &&
    editDraft.clinic_physician_id &&
    editDraft.notes.trim()
  );

  const handleLockedPhysicianChange = async (value: string) => {
    if (!communication || savingPhysicianRef.current) return;
    if (value === (communication.clinic_physician_id ?? '')) return;

    savingPhysicianRef.current = true;
    setPendingPhysicianId(value);
    setSavingPhysician(true);
    try {
      const res = await updatePatientCommunication({
        path: { communication_id: communicationId },
        // Physician alone: a locked communication refuses a body carrying any
        // other field, including one whose value has not changed.
        body: { clinic_physician_id: value },
      });
      if (res.error || !res.data) {
        throw res.error ?? new Error('Communication update returned no data.');
      }
      setCommunication(res.data);
      // Keep the autosave baseline on the persisted row, so unlocking the
      // communication later does not hand the form a stale physician.
      setSavedDraft(communicationDetailDraftFromResponse(res.data));
      onUpdated?.();
    } catch (err) {
      console.error('Failed to update physician:', err);
      showError(
        'Update Failed',
        'Failed to update physician. Please try again.'
      );
    } finally {
      savingPhysicianRef.current = false;
      setPendingPhysicianId(null);
      setSavingPhysician(false);
    }
  };

  const doSignAndSubmit = async (
    credential: ESignatureCredentialInput,
    signatureDate?: SignatureDateInput
  ) => {
    if (!communicationAttestation) return;
    setSigningLoading(true);
    try {
      // Flush pending edits first and bail if they did not persist, so we
      // never sign stale data. onSave already surfaced an error toast.
      const outcome = await saveNow();
      if (outcome !== 'saved') {
        // The save was declined or failed, which shows no toast of its own. The
        // dialog stays open only to preserve a rejected password, and there is
        // no password to preserve here — leaving it open would sit there doing
        // nothing on every further click.
        setSignAttestationOpen(false);
        return;
      }
      const result = await signTask({
        path: { task_id: communicationId },
        body: {
          ...credential,
          ...signatureDate,
          attestation_version: communicationAttestation.version,
        },
      });
      if (result.error) {
        console.error('Error signing communication:', result.error);
        showError(
          'Sign Failed',
          getBusinessErrorMessage(result) ?? 'Failed to sign. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
      // Closed only on success, so a rejected e-signature password leaves the
      // dialog and what was typed in place.
      setSignAttestationOpen(false);
    } catch (err) {
      console.error('Error signing communication:', err);
      showError('Sign Failed', 'Failed to sign. Please try again.');
    } finally {
      setSigningLoading(false);
    }
  };

  const handleOpenSignAttestation = async () => {
    // Hold the attestation dialog closed if the pending changes did not save,
    // so the user can fix and retry rather than signing stale data. onSave
    // already surfaced an error toast on a real failure.
    const outcome = await saveNow();
    if (outcome !== 'saved') return;
    setSignAttestationOpen(true);
  };

  const doApprove = async (credential: ESignatureCredentialInput) => {
    if (!communicationAttestation) return;
    setApproving(true);
    try {
      const result = await approveTask({
        path: { task_id: communicationId },
        body: {
          ...credential,
          attestation_version: communicationAttestation.version,
        },
      });
      if (result.error) {
        console.error('Error approving communication:', result.error);
        showError(
          'Approve Failed',
          getBusinessErrorMessage(result) ??
            'Failed to complete. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
      setApproveAttestationOpen(false);
    } catch (err) {
      console.error('Error approving communication:', err);
      showError('Approve Failed', 'Failed to complete. Please try again.');
    } finally {
      setApproving(false);
    }
  };

  const handleBreakSignatures = async () => {
    setBreaking(true);
    try {
      const result = await breakTaskSignatures({
        path: { task_id: communicationId },
      });
      if (result.error) {
        console.error('Error unlocking communication:', result.error);
        showError('Unlock Failed', 'Failed to unlock. Please try again.');
        return;
      }
      await fetchData();
      onUpdated?.();
    } catch (err) {
      console.error('Error unlocking communication:', err);
      showError('Unlock Failed', 'Failed to unlock. Please try again.');
    } finally {
      setBreaking(false);
    }
  };

  // ROG-3268 / EMR-1467 — a case manager hands the note back to the clinician
  // instead of approving it. Same task-generic endpoint as the visit panel.
  const handleReturnToClinician = async () => {
    setReturning(true);
    try {
      const result = await returnTaskToClinician({
        path: { task_id: communicationId },
      });
      if (result.error) {
        console.error(
          'Error returning communication to clinician:',
          result.error
        );
        showError(
          'Return Failed',
          getBusinessErrorMessage(result) ??
            'Failed to return the communication. Please try again.'
        );
        return;
      }
      await fetchData();
      onUpdated?.();
    } catch (err) {
      console.error('Error returning communication to clinician:', err);
      showError(
        'Return Failed',
        'Failed to return the communication. Please try again.'
      );
    } finally {
      setReturning(false);
    }
  };

  const handleExportPdf = async () => {
    if (pdfInFlightRef.current) return;
    pdfInFlightRef.current = true;
    setPdfLoading(true);
    let tab: ReturnType<typeof openPdfTab> = null;
    try {
      // Flush pending edits first; if they did not persist, skip the export so
      // the PDF is never built from stale server data. onSave already toasted.
      // A deleted communication has no editable form, so there is nothing to
      // flush and the save would only be a write against a deleted record.
      if (!isDeleted) {
        const outcome = await saveNow();
        if (outcome !== 'saved') return;
      }
      tab = openPdfTab();
      if (!tab) {
        return;
      }
      const response = await getPatientCommunicationPdf({
        path: { communication_id: communicationId },
        ...(isDeleted && { query: { include_deleted: true } }),
      });
      if (!response.data) {
        tab.fail('communication PDF', new Error('no PDF data received'));
        return;
      }
      tab.setBlob(
        new Blob([response.data as BlobPart], { type: 'application/pdf' })
      );
    } catch (err) {
      if (tab) {
        tab.fail('communication PDF', err);
      } else {
        console.error('Error opening communication PDF:', err);
        showError('Print Failed', 'Could not open the print view.');
      }
    } finally {
      setPdfLoading(false);
      pdfInFlightRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4' />
          <p className='text-muted-foreground'>Loading...</p>
        </div>
      </div>
    );
  }

  if (!communication) {
    return (
      <div className='flex items-center justify-center h-64'>
        <p className='text-muted-foreground'>Communication not found.</p>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full'>
      {/* Header */}
      <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b bg-muted/20'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          {onBack && (
            <Button
              variant='outline'
              size='sm'
              onClick={onBack}
              className='flex items-center gap-1.5 flex-shrink-0'
            >
              <ArrowLeft className='h-3.5 w-3.5' />
              Back
            </Button>
          )}
          <div className='h-4 w-px bg-border' />
          <div className='min-w-0 flex-1'>
            <div className='truncate text-sm font-medium text-foreground'>
              {communication.communication_type}
            </div>
            <div className='text-xs text-muted-foreground'>
              Communication date:{' '}
              {formatDateToMMDDYYYY(communication.communication_date)}
            </div>
          </div>
        </div>

        <TaskHeaderControls
          className='w-full sm:w-auto'
          statusItems={
            <TaskWorkflowStatusBadge
              status={communication.status}
              signatures={activeSignatures}
              bypasses={signatureBypasses}
            />
          }
          primaryActions={
            <>
              {canBreakSignatures && (
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => void handleBreakSignatures()}
                  disabled={breaking}
                  className='flex items-center gap-1.5'
                >
                  <Pen className='h-4 w-4' />
                  {breaking ? 'Unlocking...' : 'Unlock & Edit'}
                </Button>
              )}
              {canSign && (
                <Button
                  onClick={() => void handleOpenSignAttestation()}
                  disabled={
                    signingLoading ||
                    attestationsLoading ||
                    !communicationAttestation ||
                    saveStatus === 'saving'
                  }
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {signingLoading ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4' />
                      {getSignButtonLabel(signTargetStatus)}
                    </>
                  )}
                </Button>
              )}
              {canApprove && (
                <Button
                  onClick={() => setApproveAttestationOpen(true)}
                  disabled={
                    approving ||
                    attestationsLoading ||
                    !communicationAttestation
                  }
                  size='sm'
                  variant='default'
                  className='flex items-center gap-1.5'
                >
                  {approving ? (
                    <>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-current' />
                      Approving...
                    </>
                  ) : (
                    <>
                      <Send className='h-4 w-4' />
                      Sign & Complete
                    </>
                  )}
                </Button>
              )}
            </>
          }
          secondaryActions={[
            {
              label: pdfLoading ? 'Opening print view...' : 'Print view',
              icon: pdfLoading ? (
                <div
                  className='animate-spin rounded-full h-3 w-3 mr-2 border-b-2 border-current'
                  aria-hidden='true'
                />
              ) : (
                <ExternalLink className='h-4 w-4 mr-2' aria-hidden='true' />
              ),
              onSelect: () => void handleExportPdf(),
              disabled: pdfLoading || saveStatus === 'saving',
            },
            {
              label: 'View Signature History',
              icon: <History className='h-4 w-4 mr-2' aria-hidden='true' />,
              onSelect: () =>
                openDeferred('history', () => setHistoryOpen(true)),
            },
            ...(canViewWorkLog
              ? [
                  {
                    label: 'Work log',
                    icon: (
                      <ClipboardList
                        className='h-4 w-4 mr-2'
                        aria-hidden='true'
                      />
                    ),
                    onSelect: () =>
                      openDeferred('workLog', () => setWorkLogOpen(true)),
                  },
                ]
              : []),
            ...bypassActionItems,
            ...(canReturnToClinician
              ? [
                  {
                    label: returning ? 'Returning…' : 'Return to clinician',
                    icon: <Undo2 className='h-4 w-4 mr-2' aria-hidden='true' />,
                    onSelect: () => void handleReturnToClinician(),
                    disabled: returning,
                  },
                ]
              : []),
          ]}
        />
      </div>

      {/* Sub-header: the physician is metadata about the communication rather
          than part of the content that gets signed, so it stays editable once
          the communication locks, next to the signer info. A deleted
          communication forfeits every edit. */}
      <div className='flex items-center gap-4 px-5 py-2 border-b bg-muted/10'>
        {/* Editing while unlocked is the form's own behavior and is unchanged.
            The locked correction is offered only to the people who could unlock
            and edit anyway, so it is not a way around that authority. An
            affordance, not enforcement: see the permission decision in the PR
            body. */}
        {!isDeleted && (!isReadonly || canBreakSignatures) && (
          // The inline label shares this box with the control, so the readonly
          // row needs the extra width to keep a physician name from clipping.
          <div className={isReadonly ? 'w-72' : 'w-48'}>
            <DropdownField
              label='Physician'
              htmlFor='comm-detail-physician'
              // Inline beside the signer row so the picker and the signer read
              // as one row rather than sitting a line apart.
              labelPlacement={isReadonly ? 'left' : 'top'}
              sortOptions
              fieldSize='sm'
              placeholder='Select physician'
              searchPlaceholder='Search physicians...'
              enableFiltering
              isClearable={false}
              options={physicians.map(p => ({
                value: p.id,
                label: formatPhysicianName(p),
              }))}
              value={
                isReadonly
                  ? (pendingPhysicianId ??
                    communication.clinic_physician_id ??
                    '')
                  : editDraft.clinic_physician_id
              }
              onChange={
                isReadonly
                  ? handleLockedPhysicianChange
                  : clinic_physician_id =>
                      updateEditDraft({ clinic_physician_id })
              }
              disabled={isReadonly && savingPhysician}
            />
          </div>
        )}
        {isReadonly &&
          activeSignatures.map(sig => {
            const metadata =
              (sig.signature_metadata as Record<string, unknown> | null) ?? {};
            const displayName = signerName(sig);
            const credentials = metadata.signer_credentials as
              string | undefined | null;
            return (
              <div key={sig.id} className='flex items-center gap-2'>
                <Label className='text-xs text-muted-foreground whitespace-nowrap'>
                  Signer
                </Label>
                <span className='text-sm'>
                  {credentials ? `${displayName}, ${credentials}` : displayName}
                </span>
              </div>
            );
          })}
      </div>

      {/* Save status pill: a zero-height sticky overlay above the scrollable
          body. It floats over the top-right of the form (its own background +
          shadow keep it legible) and stays in view as the form scrolls.
          Zero height means showing/hiding the pill never shifts the layout. */}
      {!isReadonly && (
        <div className='pointer-events-none sticky top-0 z-20 flex h-0 shrink-0 justify-end px-5'>
          <AutoSavePill
            status={isAutosaveReady ? saveStatus : 'idle'}
            debounceMs={1500}
            changeToken={changeToken}
          />
        </div>
      )}

      {/* Body */}
      <div className='flex-1 overflow-auto p-4'>
        {isReadonly && isThirtyDaySummary ? (
          <div className='h-[calc(100vh-14rem)] min-h-[760px]'>
            <ReadonlyPdfView
              loadSource={loadSummaryPdfSource}
              title='30-Day Summary'
              errorText='Failed to load 30-Day Summary preview.'
            />
          </div>
        ) : isReadonly ? (
          <Card>
            <CardContent className='p-4 space-y-4'>
              <div className='grid grid-cols-2 gap-4 text-sm'>
                <div>
                  <span className='font-medium text-muted-foreground'>
                    Communication Date
                  </span>
                  <p>
                    {formatDateToMMDDYYYY(communication.communication_date)}
                  </p>
                </div>
                <div>
                  <span className='font-medium text-muted-foreground'>
                    Type
                  </span>
                  <p>{communication.communication_type}</p>
                </div>
              </div>
              {communication.notes && (
                <div>
                  <span className='text-sm font-medium text-muted-foreground'>
                    Communication
                  </span>
                  <div className='mt-1 whitespace-pre-wrap rounded-md border bg-muted/30 p-3 text-sm'>
                    {communication.notes}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className='p-4 space-y-4'>
              <div className='space-y-1.5'>
                <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  Date
                </Label>
                <DatePicker
                  date={editDate}
                  onDateChange={nextDate =>
                    updateEditDraft({
                      communication_date: nextDate
                        ? formatDateToISO(nextDate)
                        : null,
                    })
                  }
                  minDate={
                    communication.episode_start_date
                      ? parseISOToLocalDate(communication.episode_start_date)
                      : undefined
                  }
                  maxDate={
                    communication.episode_end_date
                      ? parseISOToLocalDate(communication.episode_end_date)
                      : undefined
                  }
                />
              </div>
              <div className='space-y-1.5'>
                <Label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                  Communication <span className='text-destructive'>*</span>
                </Label>
                <Textarea
                  value={editDraft.notes}
                  onChange={e => updateEditDraft({ notes: e.target.value })}
                  onBlur={() => void saveNow()}
                  placeholder='Describe the communication and any follow-up actions…'
                  className='min-h-[120px] resize-y text-sm'
                />
              </div>
            </CardContent>
          </Card>
        )}

        <Card className='mt-4'>
          <CardContent className='p-4'>
            <EpisodeNotes
              episodeId={communication.episode_id}
              canAdd={!isDeleted}
              description='Shared by every task in this episode.'
            />
          </CardContent>
        </Card>
      </div>

      <AttestationDialog
        open={signAttestationOpen}
        requiresCredential={signRequiresESignature}
        attestation={communicationAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={signingLoading}
        actionLabel={getSignButtonLabel(signTargetStatus)}
        // Clinician lane only -- see the prop's docs on AttestationDialog.
        showSignatureDate={signRequiresESignature}
        patientId={patientId}
        onConfirm={(credential, signatureDate) =>
          void doSignAndSubmit(credential, signatureDate)
        }
        onCancel={() => setSignAttestationOpen(false)}
      />

      <AttestationDialog
        open={approveAttestationOpen}
        requiresCredential={false}
        busyLabel='Approving…'
        attestation={communicationAttestation}
        signerName={user?.name ?? null}
        signerEmail={user?.email ?? null}
        isLoading={approving}
        actionLabel='Sign & Complete'
        onConfirm={credential => void doApprove(credential)}
        onCancel={() => setApproveAttestationOpen(false)}
      />

      {bypassDialog}

      <SignatureHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taskId={communicationId}
        includeDeleted={isDeleted}
      />

      <WorkLogDialog
        open={workLogOpen}
        onOpenChange={setWorkLogOpen}
        taskId={communicationId}
        taskType='communication'
        includeDeleted={isDeleted}
      />
    </div>
  );
};
